import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Clock, Plus, X, Check, CircleX, DollarSign, ChevronLeft, ChevronRight, Trash2, User } from "lucide-react";

// Get Monday of a given date
function mondayOf(d) {
  const day = new Date(d);
  const diff = (day.getDay() + 6) % 7; // Mon=0..Sun=6
  day.setDate(day.getDate() - diff);
  return day.toISOString().slice(0, 10);
}
function fmtDate(iso) {
  return new Date(iso + "T00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

const STATUS_CHIP = {
  draft:     "bg-slate-100 text-slate-700",
  approved:  "bg-blue-100 text-blue-700",
  paid:      "bg-emerald-100 text-emerald-700",
  rejected:  "bg-red-100 text-red-700",
  partially_approved: "bg-amber-100 text-amber-700",
};

export default function AdminTimesheets() {
  const [pending, setPending] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [weekData, setWeekData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadPending = async () => {
    try {
      const [p, es, ps] = await Promise.all([
        api.get("/timelogs/pending"),
        api.get("/employees"),
        api.get("/projects"),
      ]);
      setPending(p.data || []);
      setEmployees(es.data || []);
      setProjects(ps.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { loadPending(); }, []);

  const loadWeek = async () => {
    if (!selectedEmp) return;
    try {
      const r = await api.get(`/timelogs/week?employee_id=${selectedEmp}&week_start=${weekStart}`);
      setWeekData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { loadWeek(); /* eslint-disable-next-line */ }, [selectedEmp, weekStart]);

  const shiftWeek = (dir) => {
    const d = new Date(weekStart + "T00:00");
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  const approveWeek = async () => {
    setBusy(true);
    try {
      const r = await api.post("/timelogs/week/approve", { employee_id: selectedEmp, week_start: weekStart });
      toast.success(`Approved ${r.data.approved} entries — $${r.data.total_amount}`);
      await loadWeek(); await loadPending();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };
  const rejectWeek = async () => {
    const reason = prompt("Reject reason?");
    if (!reason) return;
    setBusy(true);
    try {
      const r = await api.post("/timelogs/week/reject", { employee_id: selectedEmp, week_start: weekStart, reason });
      toast.success(`Rejected ${r.data.rejected} entries`);
      await loadWeek(); await loadPending();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(false); }
  };
  const markPaid = async () => {
    if (!confirm("Mark this approved week as PAID? A payroll payment JE will be posted.")) return;
    setBusy(true);
    try {
      const r = await api.post("/timelogs/week/mark-paid", { employee_id: selectedEmp, week_start: weekStart });
      toast.success(`Paid ${r.data.paid} entries — $${r.data.total_amount}`);
      await loadWeek(); await loadPending();
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment failed"); }
    finally { setBusy(false); }
  };

  const removeEntry = async (id) => {
    if (!confirm("Delete this entry?")) return;
    try { await api.delete(`/timelogs/${id}`); await loadWeek(); await loadPending(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="admin-timesheets" className="space-y-6">
      <div>
        <p className="overline">Workforce</p>
        <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">
          Weekly Timesheets
        </h1>
        <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
          Log employee hours per project. Approve to accrue payroll (DR 5100 Labor / CR 2050 Wages Payable). Mark paid to clear liability.
        </p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Pending panel */}
        <div className="card-pb p-3 h-fit space-y-1" data-testid="pending-panel">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8] px-2 py-1.5">
            Open weeks ({pending.length})
          </div>
          {pending.length === 0 && (
            <div className="text-xs text-[#94A3B8] italic p-3 text-center">
              No pending timesheets.
            </div>
          )}
          {pending.map((w) => (
            <button
              key={`${w.employee_id}:${w.week_start}`}
              onClick={() => { setSelectedEmp(w.employee_id); setWeekStart(w.week_start); }}
              data-testid={`pending-${w.employee_id}-${w.week_start}`}
              className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
                selectedEmp === w.employee_id && weekStart === w.week_start
                  ? "bg-[#0A2540] text-white"
                  : "hover:bg-[#F8FAFC] dark:hover:bg-slate-800 text-[#0A2540] dark:text-white"
              }`}
            >
              <div className="font-medium">{w.employee_name}</div>
              <div className={`text-[10px] mt-0.5 ${
                selectedEmp === w.employee_id && weekStart === w.week_start ? "text-[#C9A356]" : "text-[#94A3B8]"
              }`}>
                Wk of {new Date(w.week_start + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })} ·
                {w.draft_hours > 0 && ` ${w.draft_hours}h draft`}
                {w.approved_hours > 0 && ` · ${w.approved_hours}h approved`}
              </div>
            </button>
          ))}
        </div>

        {/* Week detail */}
        <div className="space-y-4">
          {/* Selector */}
          <div className="card-pb p-4 flex flex-wrap items-center gap-3">
            <User className="w-4 h-4 text-[#C9A356]" />
            <select className="border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-1.5 text-sm"
                    value={selectedEmp || ""}
                    onChange={(e) => setSelectedEmp(e.target.value || null)}
                    data-testid="employee-picker">
              <option value="">— pick an employee —</option>
              {employees.map((emp) => (
                <option key={emp.user_id} value={emp.user_id}>{emp.name}</option>
              ))}
            </select>
            <div className="flex items-center gap-1">
              <button onClick={() => shiftWeek(-1)} className="p-1.5 rounded hover:bg-[#F8FAFC] dark:hover:bg-slate-800" data-testid="week-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="text-sm tabular-nums px-2 min-w-[130px] text-center">
                Week of {new Date(weekStart + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </div>
              <button onClick={() => shiftWeek(1)} className="p-1.5 rounded hover:bg-[#F8FAFC] dark:hover:bg-slate-800" data-testid="week-next">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            {selectedEmp && (
              <button onClick={() => setAdding(true)} className="btn-accent text-sm inline-flex items-center gap-1 ml-auto" data-testid="add-timelog-btn">
                <Plus className="w-4 h-4" /> Add hours
              </button>
            )}
          </div>

          {!selectedEmp ? (
            <div className="card-pb p-10 text-center text-[#94A3B8] italic">
              Pick an employee (or a pending week) to see their timesheet.
            </div>
          ) : !weekData ? (
            <div className="text-sm text-[#94A3B8]">Loading…</div>
          ) : (
            <>
              {/* Week summary */}
              <div className="grid grid-cols-3 gap-3">
                <StatCard label="Total hours" value={`${weekData.total_hours}h`} icon={Clock} />
                <StatCard label="Total amount" value={`$${weekData.total_amount.toLocaleString()}`} icon={DollarSign} />
                <div className="card-pb p-4">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">Status</div>
                  <div className="mt-2">
                    <span className={`text-xs font-bold uppercase tracking-wide px-2 py-1 rounded ${STATUS_CHIP[weekData.week_status] || "bg-slate-100 text-slate-700"}`}
                          data-testid="week-status-chip">
                      {weekData.week_status.replace(/_/g, " ")}
                    </span>
                  </div>
                </div>
              </div>

              {/* 7-day grid */}
              <div className="card-pb overflow-hidden">
                <div className="grid grid-cols-7 border-b border-[#E2E8F0] dark:border-slate-700">
                  {weekData.days.map((d) => (
                    <div key={d} className="text-[10px] uppercase tracking-wider text-center py-2 text-[#94A3B8] font-bold">
                      {new Date(d + "T00:00").toLocaleDateString(undefined, { weekday: "short" })}
                      <div className="text-[10px] text-[#0A2540] dark:text-white">
                        {new Date(d + "T00:00").getDate()}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 min-h-[120px]">
                  {weekData.days.map((d) => (
                    <div key={d} className="border-r border-[#E2E8F0] dark:border-slate-700 last:border-r-0 p-1.5 space-y-1" data-testid={`day-cell-${d}`}>
                      {(weekData.by_day[d] || []).map((tl) => (
                        <div key={tl.timelog_id} data-testid={`tl-row-${tl.timelog_id}`}
                             className={`text-[10px] rounded p-1.5 ${
                               tl.status === "rejected" ? "bg-red-50 line-through opacity-60"
                               : tl.status === "paid" ? "bg-emerald-50"
                               : tl.status === "approved" ? "bg-blue-50"
                               : "bg-slate-50 dark:bg-slate-800"
                             }`}>
                          <div className="font-bold text-[#0A2540] dark:text-white">{tl.hours}h</div>
                          <div className="truncate text-[#475569] dark:text-slate-400" title={tl.project_name}>{tl.project_name}</div>
                          <div className="flex justify-between items-center mt-0.5">
                            <span className="tabular-nums">${tl.amount}</span>
                            {tl.status === "draft" && (
                              <button onClick={() => removeEntry(tl.timelog_id)}
                                      className="text-red-500 hover:text-red-700"
                                      data-testid={`del-tl-${tl.timelog_id}`}>
                                <Trash2 className="w-2.5 h-2.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 justify-end">
                {(weekData.week_status === "draft" || weekData.week_status === "partially_approved") && weekData.status_counts.draft > 0 && (
                  <>
                    <button onClick={rejectWeek} disabled={busy}
                            className="text-sm px-4 py-1.5 border border-red-300 text-red-600 rounded hover:bg-red-50 inline-flex items-center gap-1 disabled:opacity-50"
                            data-testid="reject-week-btn">
                      <CircleX className="w-3 h-3" /> Reject week
                    </button>
                    <button onClick={approveWeek} disabled={busy}
                            className="btn-accent text-sm inline-flex items-center gap-1 disabled:opacity-50"
                            data-testid="approve-week-btn">
                      <Check className="w-3.5 h-3.5" /> Approve week
                    </button>
                  </>
                )}
                {weekData.week_status === "approved" && (
                  <button onClick={markPaid} disabled={busy}
                          className="btn-accent text-sm inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid="mark-paid-btn">
                    <DollarSign className="w-3.5 h-3.5" /> Mark paid
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {adding && (
        <AddModal
          employeeId={selectedEmp}
          weekDays={weekData?.days || []}
          projects={projects}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); loadWeek(); loadPending(); }}
        />
      )}
    </div>
  );
}

function AddModal({ employeeId, weekDays, projects, onClose, onSaved }) {
  const [d, setD] = useState(weekDays[0] || "");
  const [pid, setPid] = useState(projects[0]?.project_id || "");
  const [hours, setHours] = useState(8);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/timelogs", { employee_id: employeeId, project_id: pid, date: d, hours: Number(hours), notes });
      toast.success("Hours added");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const cls = "w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-1.5 text-sm";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-[#0A2540] dark:text-white">Add hours</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Day *</label>
            <select className={cls} value={d} onChange={(e) => setD(e.target.value)} data-testid="add-tl-date">
              {weekDays.map((day) => (
                <option key={day} value={day}>{fmtDate(day)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Project *</label>
            <select className={cls} value={pid} onChange={(e) => setPid(e.target.value)} data-testid="add-tl-project">
              {projects.map((p) => (
                <option key={p.project_id} value={p.project_id}>{p.title || p.project_id}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Hours *</label>
            <input type="number" min="0.25" max="24" step="0.25" className={cls} value={hours}
                   onChange={(e) => setHours(e.target.value)} data-testid="add-tl-hours" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Notes</label>
            <input className={cls} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <button onClick={save} disabled={saving} className="btn-accent text-sm w-full disabled:opacity-50" data-testid="add-tl-save">
            {saving ? "Saving…" : "Add hours"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="card-pb p-4">
      <div className="inline-flex items-center gap-1.5">
        <Icon className="w-3 h-3 text-[#C9A356]" />
        <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">{label}</div>
      </div>
      <div className="text-xl font-heading mt-1 text-[#0A2540] dark:text-white">{value}</div>
    </div>
  );
}
